import type { ComponentType } from "react";
import { MCQ_FAMILY_TYPES } from "../content-types";
import type { QuestionRendererProps } from "./types";
import { McqRenderer } from "./mcq";
import { TrueFalseRenderer } from "./true-false";
import { FillBlankRenderer } from "./fill-blank";
import { FlashcardRenderer } from "./flashcard";
import { ShortAnswerRenderer } from "./short-answer";
import { OrderingRenderer } from "./ordering";
import { DefinitionMatchRenderer } from "./definition-match";
import { VocabPairRenderer } from "./vocab-pair";
import { InflectionTableRenderer } from "./inflection-table";
import { ReadingComprehensionRenderer } from "./reading-comprehension";
import { ErrorIdentificationRenderer } from "./error-identification";
import { ConceptMapRenderer } from "./concept-map";
import { CauseEffectRenderer } from "./cause-effect";
import { SocraticChainRenderer } from "./socratic-chain";
import { EquationBuilderRenderer } from "./equation-builder";
import { DerivationRenderer } from "./derivation";
import { ImageLabelingRenderer } from "./image-labeling";
import { CrosswordRenderer } from "./crossword";
import { VocabPairPoolRenderer } from "./vocab-pair-pool";
import { ArticleDrillBatchRenderer } from "./article-drill-batch";
import { RedemittelLabelRenderer } from "./redemittel-label";
import { ConnectorClassifyRenderer } from "./connector-classify";
import { TransformationPairRenderer } from "./transformation-pair";
import { TypedRecallFillRenderer } from "./typed-recall-fill";
import { SentenceReorderClausesRenderer } from "./sentence-reorder-clauses";
import { HauptaussagenRenderer } from "./hauptaussagen";
import { ParagraphGapSelectRenderer } from "./paragraph-gap-select";
import { LesenStatementCompareRenderer } from "./lesen-statement-compare";
import { LesenGuidedPassageRenderer } from "./lesen-guided-passage";
import { UnknownRenderer } from "./unknown";

const REGISTRY: Record<string, ComponentType<QuestionRendererProps>> = {
  multiple_choice: McqRenderer,
  true_false: TrueFalseRenderer,
  fill_blank: FillBlankRenderer,
  cloze_passage: FillBlankRenderer,
  flashcard: FlashcardRenderer,
  short_answer: ShortAnswerRenderer,
  ordering: OrderingRenderer,
  definition_match: DefinitionMatchRenderer,
  vocab_pair: VocabPairRenderer,
  inflection_table: InflectionTableRenderer,
  reading_comprehension: ReadingComprehensionRenderer,
  error_identification: ErrorIdentificationRenderer,
  equation_builder: EquationBuilderRenderer,
  derivation: DerivationRenderer,
  image_labeling: ImageLabelingRenderer,
  crossword: CrosswordRenderer,
  vocab_pair_pool: VocabPairPoolRenderer,
  article_drill_batch: ArticleDrillBatchRenderer,
  redemittel_label: RedemittelLabelRenderer,
  connector_classify: ConnectorClassifyRenderer,
  transformation_pair: TransformationPairRenderer,
  typed_recall_fill: TypedRecallFillRenderer,
  sentence_reorder_clauses: SentenceReorderClausesRenderer,
  hauptaussagen_passage: HauptaussagenRenderer,
  paragraph_gap_select: ParagraphGapSelectRenderer,
  lesen_statement_compare_passage: LesenStatementCompareRenderer,
  lesen_guided_passage: LesenGuidedPassageRenderer,
  socratic_chain: SocraticChainRenderer,
};

// The single-select MCQ-family types all render through McqRenderer.
for (const t of MCQ_FAMILY_TYPES) REGISTRY[t] = McqRenderer;
// …except concept_map, which shows the two concepts side by side, and
// cause_effect, which shows the two statements joined by a connector.
REGISTRY.concept_map = ConceptMapRenderer;
REGISTRY.cause_effect = CauseEffectRenderer;

export function questionRendererFor(type: string): ComponentType<QuestionRendererProps> {
  return REGISTRY[type] ?? UnknownRenderer;
}
